import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Box, Typography, ButtonBase } from '@mui/material';
import { useMediaQuery } from 'react-responsive';
import Loader from '../../components/loader/Loader';
import ModalEditCategories from '../modals/modalEditImages/ModalEditCategories';

const CATALOGUE_ID = '64ef4986d269731b208134bf';
const CATALOGUE_BASE = `/market/categoria/${CATALOGUE_ID}?nombre=MARKET`;

function buildQueryFromImage(image = {}) {
  // Helper para obtener el primer valor no vacío entre varias keys posibles
  const pick = (...keys) =>
    keys.map((k) => image?.[k]).find((v) => v != null && v !== '');

  const cat = pick('categoria', 'Categoria');
  const subcat = pick('subCategoria', 'Subcategoria', 'subcategoria');
  const marca = pick('marca', 'Marca');
  const q = pick('termino', 'Termino', 'q');

  // Si la tarjeta apunta explícitamente a Gangas, redireccioná a /ofertas
  if (cat && /ganga/i.test(cat)) {
    return '/ofertas';
  }

  const sp = new URLSearchParams();
  if (cat) sp.set('cat', String(cat));
  if (subcat) sp.set('subcat', String(subcat));
  if (marca) sp.set('marca', String(marca));
  if (q) sp.set('q', String(q));

  const qs = sp.toString();
  return qs ? `${CATALOGUE_BASE}&${qs}` : CATALOGUE_BASE;
}

export default function CategorySection() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 700 });
  const [loader, setLoader] = React.useState(true);
  const [images, setImages] = React.useState([]);
  const [showModalEditData, setShowModalEditData] = React.useState(false);
  const [imageChangeKey, setImageChangeKey] = React.useState(0);

  const user = useSelector((state) => state.user.userInfo);
  const isSuperAdmin = (user?.role || '') === 'SUPERADMIN_ROLE';

  const handleOpenModalEditCategory = () => setShowModalEditData(true);
  const handleCloseModalEditCategory = () => setShowModalEditData(false);

  React.useEffect(() => {
    const cargarImagenes = async () => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACK_URL}/api/imagenes?tipo=INFO_CAT`
      );
      setImages(data.imagenes || []);
      setLoader(false);
    };
    cargarImagenes();
  }, [imageChangeKey]);

  const handleNavigateCatalogue = (image) => {
    const url = buildQueryFromImage(image);
    navigate(url);
  };

  const bannerImage = images.find(
    (img) => img?.nombre?.toUpperCase() === 'BANNER'
  );
  const otherImages = images.filter(
    (img) => img?.nombre?.toUpperCase() !== 'BANNER'
  );

  const firstThree = otherImages.slice(0, 3);
  const lastThree = otherImages.slice(3, 6);

  const renderImage = (image, index) => (
    <Box
      key={index}
      display="flex"
      flexDirection="column"
      alignItems="center"
      margin="10px"
      sx={{ width: isMobile ? '25%' : '22%' }}
    >
      <ButtonBase
        onClick={() => handleNavigateCatalogue(image)}
        sx={{
          width: '100%',
          zIndex: 2,
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'block',
        }}
      >
        <Box
          component="img"
          src={image?.url}
          alt={image?.nombre || `img-${index}`}
          sx={{
            width: '100%',
            aspectRatio: '3 / 4',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': { transform: 'scale(1.05)' },
          }}
        />
      </ButtonBase>

      <Typography
        variant="subtitle1"
        sx={{
          marginTop: '8px',
          fontWeight: 400,
          fontSize: isMobile ? '14px' : '24px',
          textTransform: 'uppercase',
          textAlign: 'center',
        }}
      >
        {image?.titulo}
      </Typography>
    </Box>
  );

  return (
    <div className="testimonios">
      {loader ? (
        <Loader />
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ marginTop: '5vh' }}
        >
          <Typography
            variant="h5"
            sx={{
              justifySelf: 'flex-start',
              alignSelf: 'center',
              fontWeight: 400,
              marginBottom: '30px',
              marginLeft: '30px',
              textTransform: 'uppercase',
              fontFamily: 'Poppins',
              fontSize: isMobile ? '16px' : '28px',
            }}
          >
            Elegí tus categorías
          </Typography>

          {/* Primeras 3 imágenes */}
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="space-between"
            width={!isMobile ? '80%' : '100%'}
          >
            {firstThree.map(renderImage)}
          </Box>

          {/* Banner */}
          {bannerImage && (
            <Box
              sx={{
                width: '100%',
                position: 'relative',
                marginY: '20px',
              }}
            >
              <ButtonBase
                onClick={() => handleNavigateCatalogue(bannerImage)}
                sx={{ width: '100%', display: 'block' }}
              >
                <Box
                  component="img"
                  src={bannerImage?.url}
                  alt="Banner"
                  sx={{
                    width: '100vw',
                    objectFit: 'cover',
                    cursor: 'pointer',
                  }}
                />
              </ButtonBase>

              {isSuperAdmin && (
                <Box
                  onClick={() => setShowModalEditData(true)}
                  sx={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    width: '35px',
                    height: '35px',
                    backgroundColor: '#225522',
                    borderRadius: '50%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    zIndex: 2,
                  }}
                >
                  <i
                    className="bi bi-pencil"
                    style={{ color: '#fff', fontSize: '16px' }}
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Últimas 3 imágenes */}
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="space-between"
            width={!isMobile ? '80%' : '100%'}
          >
            {lastThree.map(renderImage)}
          </Box>
        </Box>
      )}

      <ModalEditCategories
        show={showModalEditData}
        handleClose={() => setShowModalEditData(false)}
        data={images}
      />
    </div>
  );
}
